import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  AttentionBox,
  TextField,
  Typography,
  InputLabel,
  Checkbox,
  Loading,
  useRightSideBar,
  CustomIcon,
  Box
} from '@p81-common/ui';
import { CompletionScreen } from './parts/shared/CompletionScreen';
import { NeutralInfoBox } from './parts/shared/ui';
import { postRequest } from '../../../utils/requests';
import { Trans, useTranslation } from 'react-i18next';
import { useRegion } from '../../../contexts/RegionContext';
import { validateUUID } from '../../../utils/validation';
import { createClasses } from './styles';
import { links } from '../../../data/constants';

const initialErrorState = {
  error: false,
  message: ''
};

const WarningMessage = ({
  children,
  message
}: PropsWithChildren<{ message: string }>) => {
  const classes = createClasses();
  return (
    <div className={classes.warning}>
      <CustomIcon
        src={`${links.imagesBaseUrl}/icons/icon_alertExagon.svg`}
        color="#F24E4E"
        size="xlarge"
        className={classes.warningIcon}
      />
      <Typography
        variant="subtitle1"
        dangerouslySetInnerHTML={{ __html: message }}
      />
      {children}
    </div>
  );
};

export interface AssociateTenantsProps {
  companyName: string;
  tenantId: string;
  updateDataFn(): void;
  onBack?: () => void;
  /**
   * 'parentWorkspace' renders the MSSP Parent direct-association variant:
   * a not-supported warning replaces the region attention box and the
   * submit button reads "Proceed Anyway".
   */
  variant?: 'default' | 'parentWorkspace';
  /** When provided, success is delegated to the parent instead of showing
   * the built-in success screen. Receives the associated tenant UUID. */
  onAssociated?: (tenantUUID: string) => void;
}

type AssociationConflict =
  | 'TENANT_UUID_ALREADY_ASSOCIATED'
  | 'EMAIL_DOMAIN_MISMATCH'
  | null;

const PARENT_WORKSPACE = 'tenantsAssociation.mspParentWorkspace';

const AssociateTenants = ({
  companyName,
  tenantId,
  onBack,
  updateDataFn,
  variant = 'default',
  onAssociated
}: PropsWithChildren<AssociateTenantsProps>) => {
  const [tenantUUID, setTenantUUID] = useState('');
  const [fieldError, setFieldError] = useState(initialErrorState);
  const inputTouched = useRef(false);
  const [associationChecked, setAssociationChecked] = useState(false);
  const [associationPending, setAssociationPending] = useState(false);
  const [associationConflict, setAssociationConflict] =
    useState<AssociationConflict>(null);
  const [associationConflictTenantId, setAssociationConflictTenantId] =
    useState<string>('');
  const [associationDone, setAssociationDone] = useState(false);
  const { t } = useTranslation('activity');
  const { region } = useRegion();
  const { setOpenSecondary, setShowBottomActionBar, clearComponentsArray } =
    useRightSideBar();

  const closeSidebar = useCallback(() => {
    setOpenSecondary(false);
    clearComponentsArray();
  }, [setOpenSecondary, clearComponentsArray]);
  const classes = createClasses();

  const validateTenantUUID = (value: string) => {
    if (!value) {
      return {
        error: true,
        message: t('tenantsAssociation.errors.REQUIRED')
      };
    }

    if (!validateUUID(value)) {
      return {
        error: true,
        message: t('tenantsAssociation.errors.INVALID_UUID')
      };
    }

    return initialErrorState;
  };

  const handleInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!inputTouched.current) {
      inputTouched.current = true;
    }

    const { value } = e.target;
    const errorObj = validateTenantUUID(value);
    if (errorObj.error) {
      setFieldError(errorObj);
    } else {
      setFieldError(initialErrorState);
    }

    setTenantUUID(value);
  };

  const handleCheckbox = () => {
    setAssociationChecked(prev => !prev);
  };

  const handleRequestError = useCallback(
    e => {
      setAssociationPending(false);
      if (e.response.status === 409) {
        setAssociationConflict(e.response.data.message);
        setAssociationConflictTenantId(e.response.data.body.tenantId);
        return;
      }
      if (e.response.status === 404) {
        return setFieldError({
          error: true,
          message: t('tenantsAssociation.errors.NOT_FOUND')
        });
      }
      setFieldError({
        error: true,
        message: t('tenantsAssociation.errors.ASSOCIATION_FAILED')
      });
    },
    [t]
  );

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault();
      setAssociationPending(true);

      try {
        const res = await postRequest('/customer/cp/association', {
          tenantUUID,
          tenantId
        });
        setAssociationPending(false);

        if (res.statusCode === 200) {
          updateDataFn();
          if (onAssociated) {
            onAssociated(tenantUUID);
          } else {
            setAssociationDone(true);
          }
        }
      } catch (e) {
        handleRequestError(e);
      }
    },
    [handleRequestError, tenantId, tenantUUID, updateDataFn, onAssociated]
  );

  const disableSubmit = useCallback(() => {
    const basicValidationFailed =
      fieldError.error ||
      !associationChecked ||
      associationPending ||
      !tenantUUID;

    if (associationConflict) {
      return (
        basicValidationFailed ||
        associationConflict === 'TENANT_UUID_ALREADY_ASSOCIATED'
      );
    }

    return basicValidationFailed;
  }, [
    fieldError.error,
    associationChecked,
    associationPending,
    tenantUUID,
    associationConflict
  ]);

  useEffect(() => {
    // The success screen renders its own in-content Done button, so keep
    // the BottomActionBar hidden while it is shown.
    if (associationDone) {
      return;
    }

    setShowBottomActionBar({
      okButtonProps: {
        text:
          variant === 'parentWorkspace'
            ? t(`${PARENT_WORKSPACE}.PROCEED`)
            : t('tenantsAssociation.ASSOCIATE'),
        disabled: disableSubmit(),
        type: 'submit',
        onClick: e => handleSubmit(e)
      },
      cancelButtonProps: onBack
        ? { text: t('tenantsAssociation.BACK'), onClick: onBack }
        : { text: t('buttons.CANCEL'), onClick: closeSidebar },
      isSideBar: true
    });

    return () => setShowBottomActionBar(null);
  }, [
    associationDone,
    associationPending,
    disableSubmit,
    handleSubmit,
    closeSidebar,
    onBack,
    setShowBottomActionBar,
    t,
    variant
  ]);

  if (associationDone) {
    return (
      <Box>
        {!onBack && (
          <Typography variant="h2" className={classes.heading}>
            <Trans
              i18nKey="activity:tenantsAssociation.TITLE"
              values={{ companyName }}
              components={{ span: <span /> }}
            />
          </Typography>
        )}
        <CompletionScreen
          title={t('tenantsAssociation.successScreen.TITLE')}
          body={t('tenantsAssociation.successScreen.BODY', { tenantUUID })}
          actionLabel={t('tenantsAssociation.successScreen.DONE')}
          onAction={closeSidebar}
        />
      </Box>
    );
  }

  return (
    <Box>
      <form className={classes.container} onSubmit={handleSubmit}>
        {!onBack && (
          <>
            <Typography variant="h2" className={classes.heading}>
              <Trans
                i18nKey="activity:tenantsAssociation.TITLE"
                values={{ companyName }}
                components={{ span: <span /> }}
              />
            </Typography>
            <Typography variant="subtitle1" className={classes.subtitle}>
              {t('tenantsAssociation.PAGE_SUBTITLE')}
            </Typography>
          </>
        )}

        <div className={classes.regionAttention}>
          {variant === 'parentWorkspace' ? (
            <NeutralInfoBox title={t(`${PARENT_WORKSPACE}.WARNING_TITLE`)}>
              <Typography variant="body2" className={classes.approachText}>
                {t(`${PARENT_WORKSPACE}.WARNING_BODY`)}
              </Typography>
              <Typography
                variant="body2"
                className={`${classes.approachText} ${classes.approachTitle}`}
              >
                {t(`${PARENT_WORKSPACE}.RECOMMENDED_APPROACH_TITLE`)}
              </Typography>
              <Box component="ol" className={classes.approachList}>
                {(
                  t(`${PARENT_WORKSPACE}.RECOMMENDED_APPROACHES`, {
                    returnObjects: true
                  }) as string[]
                ).map(item => (
                  <Typography component="li" variant="body2" key={item}>
                    {item}
                  </Typography>
                ))}
              </Box>
            </NeutralInfoBox>
          ) : (
            <AttentionBox
              compact
              colorScheme="primary"
              iconUrl={`${links.imagesBaseUrl}/png/illus_OrgSec_alert.png`}
              subtitle={t('tenantsAssociation.REGION_ATTENTION', {
                region: region.code
              })}
            />
          )}
        </div>

        <TextField
          value={tenantUUID}
          label={t('tenantsAssociation.INPUT_LABEL')}
          required
          onChange={handleInput}
          error={inputTouched.current && fieldError.error}
          helperText={fieldError.message}
          fullWidth
        />

        <Typography variant="caption" className={classes.inputHint}>
          {t('tenantsAssociation.INPUT_HINT')}
        </Typography>

        <InputLabel className={classes.confirmCheckbox}>
          <Checkbox checked={associationChecked} onChange={handleCheckbox} />
          <Typography variant="body2">
            {t('tenantsAssociation.CONFIRM_CHECKBOX')}
          </Typography>
        </InputLabel>

        {associationConflict && (
          <WarningMessage
            message={t(`tenantsAssociation.errors.${associationConflict}`, {
              tenantId: associationConflictTenantId
            })}
          />
        )}

        {associationPending && (
          <div className={classes.loader}>
            <Loading />
          </div>
        )}
      </form>
    </Box>
  );
};

export default AssociateTenants;
